import { toast } from 'atoms/Toast'
import {
  IntegrationTemplate,
  IntegrationOnboardConfirmationResponse,
} from 'common/types/kraken-core/IntegrationTemplate'
import React, { useCallback, useEffect, useState } from 'react'
import {
  confirmIntegrationOnboard,
  getTemplate,
  ConfirmIntegrationOnboarding,
} from 'services/kraken-core/integration_template/integration.template.service'

export interface TemplateOnboardContextProps {
  templateId: string
  children: React.ReactNode
}

export interface ConfirmationReponse {
  messageId?: string
  correlationCode?: string
}

export interface TemplateOnboardContextState {
  template?: IntegrationTemplate
  confirmationResponse?: ConfirmationReponse
  isLoading?: boolean
  confirmOnboard?: (data: any) => Promise<boolean>
}

export const TemplateOnboardContext = React.createContext<TemplateOnboardContextState>({})

function TemplateOnboardContextWrapper({ children, templateId }: TemplateOnboardContextProps) {
  const [template, setTemplate] = useState<IntegrationTemplate>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [confirmationResponse, setConfirmationResponse] = useState<ConfirmationReponse>({})

  // Actions
  const getTemplateAndUpdateState = useCallback(async (templateId: string) => {
    setIsLoading(true)
    await getTemplate(templateId).then(r => {
      const { response } = r
      if (r.error) {
        toast.error(r.error.message)
      }

      const template: IntegrationTemplate = response?.data
      setTemplate(template)
    })
    setIsLoading(false)
  }, [])

  async function confirmOnboard(data: any): Promise<boolean> {
    if (!template) {
      toast.error('There is no template selected')
      return false
    }
    setIsLoading(true)
    const confirmarion: ConfirmIntegrationOnboarding = {
      template,
      data,
    }
    let result = false
    await confirmIntegrationOnboard(confirmarion).then(r => {
      if (r.error) {
        toast.error(String(r.error))
        return
      }
      toast.success('Successfully confirmed')
      const data: IntegrationOnboardConfirmationResponse = r.response?.data
      result = true
      setConfirmationResponse(s => ({
        ...s,
        messageId: data.id || '',
        correlationCode: data.correlation_code || '',
      }))
    })
    setIsLoading(false)
    return result
  }

  // State
  const state: TemplateOnboardContextState = {
    isLoading,
    template,
    confirmationResponse,
    confirmOnboard,
  }

  // Effects
  useEffect(() => {
    getTemplateAndUpdateState(templateId)
  }, [getTemplateAndUpdateState, templateId])

  return <TemplateOnboardContext.Provider value={state}>{children}</TemplateOnboardContext.Provider>
}

export default TemplateOnboardContextWrapper
